import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Search, Tag } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import PublicHeader from '../components/PublicHeader';
import { PublicFooter } from '../components/SEOLandingTemplate';
import FloatingWhatsapp from '../components/FloatingWhatsapp';
import RequestQuoteDialog from '../components/RequestQuoteDialog';
import { Input } from '../components/ui/input';
import { COMPANY, organizationJsonLd } from '../content/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [params, setParams] = useSearchParams();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [search, setSearch] = useState(params.get('q') || '');

  const category = params.get('category') || '';
  const tag = params.get('tag') || '';

  const load = () => {
    const q = new URLSearchParams();
    if (category) q.set('category', category);
    if (tag) q.set('tag', tag);
    if (search) q.set('q', search);
    axios.get(`${API}/blog/?${q}`).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, tag, params.get('q')]);

  useEffect(() => {
    axios.get(`${API}/blog/categories`).then((r) => setCategories(r.data)).catch(() => {});
    axios.get(`${API}/blog/tags`).then((r) => setTags(r.data)).catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (search) next.set('q', search); else next.delete('q');
    setParams(next);
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: `${COMPANY.name} Blog`,
    url: `${COMPANY.site}/blog`,
    description: 'Guides, tips and insights on food procurement, hospitality and produce in Rwanda.',
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Blog"
        description={`Insights, guides and news from ${COMPANY.name} on food procurement, hospitality and fresh produce in Rwanda.`}
        path="/blog"
        jsonLd={[jsonLd, organizationJsonLd()]}
      />
      <PublicHeader onQuote={() => setQuoteOpen(true)} />

      <section className="max-w-6xl mx-auto px-4 py-14">
        <h1 className="text-4xl sm:text-5xl font-serif font-bold text-neutral-900">Bison Blog</h1>
        <p className="text-lg text-neutral-600 mt-3 max-w-2xl">
          Guides, tips and insights on food procurement, hospitality and fresh produce in Rwanda.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-6">
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 items-start">
          <div className="relative flex-1 w-full max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              placeholder="Search articles…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="blog-search"
            />
          </div>
          {(category || tag) && (
            <button
              type="button"
              onClick={() => setParams({})}
              className="text-sm text-emerald-700 underline"
              data-testid="blog-clear-filter"
            >
              Clear filter
            </button>
          )}
        </form>

        {categories.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {categories.map((c) => (
              <Link
                key={c}
                to={`/blog?category=${encodeURIComponent(c)}`}
                className={
                  'px-3 py-1 rounded-full text-xs font-medium border ' +
                  (category === c
                    ? 'bg-emerald-700 border-emerald-700 text-white'
                    : 'border-neutral-200 text-neutral-700 hover:border-emerald-700')
                }
                data-testid={`blog-cat-${c}`}
              >
                {c}
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-16">
        {posts.length === 0 ? (
          <div className="text-center py-16 text-neutral-500">
            No posts yet — check back soon.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className="group block border border-neutral-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-emerald-700 transition-all"
                data-testid={`blog-post-${p.slug}`}
              >
                {p.cover_image_url && (
                  <div className="aspect-video overflow-hidden bg-neutral-100">
                    <img
                      src={p.cover_image_url}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                )}
                <div className="p-5">
                  <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">
                    {p.category}
                  </p>
                  <h2 className="mt-1 text-xl font-serif font-bold text-neutral-900 group-hover:text-emerald-700">
                    {p.title}
                  </h2>
                  <p className="text-sm text-neutral-600 mt-2 line-clamp-3">{p.excerpt}</p>
                  <div className="mt-3 flex items-center justify-between text-xs text-neutral-500">
                    <span>{p.author_name}</span>
                    <span className="inline-flex items-center gap-1 text-emerald-700">
                      Read <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <PublicFooter />
      <FloatingWhatsapp />
      <RequestQuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
}
